import { useState, useRef, useEffect } from 'react';

/**
 * Mécanique commune des modales à formulaire (AddEleve, Addgroupe) :
 *  - state `values` / `errors` / `isSubmitting` ;
 *  - reset + focus du 1er champ à l'ouverture (`show` passe à true) ;
 *  - Échap → ferme, Ctrl+Entrée → valide.
 * Le JSX des champs, la validation et le payload restent fournis par l'appelant.
 *
 * @param {Object} cfg
 * @param {boolean} cfg.show
 * @param {Object} cfg.initialValues          valeurs initiales du formulaire
 * @param {(values)=>Object} [cfg.validate]   → objet d'erreurs (`{}` si OK)
 * @param {(values)=>Promise} cfg.onSubmit    payload construit par l'appelant
 * @param {()=>void} cfg.onClose
 * @returns {{ values, setValues, errors, setErrors, handleChange, handleSubmit, handleKeyDown, isSubmitting, modalRef, firstFieldRef }}
 */
export default function useFormModal({ show, initialValues, validate, onSubmit, onClose }) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);
  const firstFieldRef = useRef(null);
  // Réf vers les dernières valeurs initiales sans re-déclencher l'effet d'ouverture.
  const initialRef = useRef(initialValues);
  initialRef.current = initialValues;

  // Ouverture : reset du formulaire + focus du 1er champ.
  useEffect(() => {
    if (!show) return undefined;
    setValues(initialRef.current);
    setErrors({});
    modalRef.current?.focus();
    const t = setTimeout(() => firstFieldRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handleSubmit = async () => {
    const newErrors = validate ? validate(values) : {};
    if (newErrors && Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isSubmitting) onClose();
    else if (e.key === 'Enter' && e.ctrlKey && !isSubmitting) handleSubmit();
  };

  return { values, setValues, errors, setErrors, handleChange, handleSubmit, handleKeyDown, isSubmitting, modalRef, firstFieldRef };
}
